import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Stripe } from '../../components/shared/http/request-interfaces';

export interface CreateConnectAccountResponse {
    clientSecret: string;
    publishableKey: string;
}

export interface AccountSessionResponse {
    clientSecret: string;
    publishableKey: string;
}

export interface ConnectAccountStatusResponse {
    status: 'verified' | 'pending' | 'incomplete' | 'restricted' | 'not_started';
    treasuryCapability: string | null;
    requirementsCurrentlyDue: string[];
    requirementsPendingVerification: string[];
    disabledReason: string | null;
    /**
     * SD-10156: the `acct_` id, served ONLY to supers (everyone else — and older cores — gets
     * null/undefined). Keying the dashboard-link affordance off this field means the server's role
     * check is the authority, not a client-side one.
     */
    stripeConnectAccountId?: string | null;
}

/**
 * One stored organization detail PayHOA will not send to Stripe (SD-9498).
 *
 * `field` is Stripe's own dotted name (`company.address`, `company.tax_id`) — the same vocabulary
 * that comes back in `requirements.currently_due` — and `reason` says what is wrong with the
 * stored value. Neither is user-facing: the notice renders the label mapped from `field`, never
 * the value itself.
 */
export interface ConnectPrefillOmission {
    field: string;
    reason: string;
}

export interface ConnectPrefillCheckResponse {
    omissions: ConnectPrefillOmission[];
}

@Injectable({
    providedIn: 'root',
})
export class StripeConnectService {
    constructor(private _http: ApplicationHttpClient) {}

    /**
     * Creates a new Stripe Connect account for the organization and returns
     * an account session client secret for the embedded onboarding component.
     */
    public createConnectAccount(
        request: Stripe.ConnectAccountSessions.CreateConnectAccountSessionRequest,
    ): Observable<CreateConnectAccountResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.StripeModule.CreateConnectAccount(request.organizationId),
        );
        return this._http.post<CreateConnectAccountResponse>(url, JSON.stringify(request));
    }

    /**
     * Creates an account session for the embedded onboarding component.
     * The client secret is used to initialize the Stripe Connect embedded component.
     */
    public createAccountSession(
        request: Stripe.ConnectAccountSessions.ReadConnectAccountSessionRequest,
    ): Observable<AccountSessionResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.StripeModule.CreateConnectAccountSession(
                request.organizationId,
                request.stripeConnectAccountId,
            ),
        );
        return this._http.post<AccountSessionResponse>(url, JSON.stringify(request));
    }

    public getConnectAccountStatus(organizationId: number): Observable<ConnectAccountStatusResponse> {
        const url = Routes.MakeLegFiCoreUrl(Routes.StripeModule.ConnectAccountStatus(organizationId));
        return this._http.get<ConnectAccountStatusResponse>(url);
    }

    /**
     * Which stored organization details will be withheld from Stripe when the Connect account is
     * created (SD-9498).
     *
     * Read-only and Stripe-free — it previews the same resolution the create call performs, so the
     * admin can be told before the embedded onboarding component is mounted. Returns an empty list
     * for an org that already has a Connect account: the prefill is spent by then.
     */
    public getPrefillCheck(organizationId: number): Observable<ConnectPrefillCheckResponse> {
        const url = Routes.MakeLegFiCoreUrl(Routes.StripeModule.ConnectPrefillCheck(organizationId));
        return this._http.get<ConnectPrefillCheckResponse>(url);
    }

    /**
     * Creates an account session for a specific embedded component (e.g., issuing-cards-list).
     * Resolves the Connect account automatically — only needs org ID and usage.
     */
    public createComponentSession(
        organizationId: number,
        usage: string,
    ): Observable<AccountSessionResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.StripeModule.ComponentSession(organizationId),
        );
        return this._http.post<AccountSessionResponse>(url, JSON.stringify({ usage }));
    }
}
